import { type ReactNode } from 'react'
import Link from 'next/link'
import { Badge } from '@ui/base/badge'
import { Button } from '@ui/base/button'

import { bodyText, display, eyebrow, lead, mutedText } from './typography'

/** Строка боковой карточки первого экрана: подпись прописными, крупное значение и пояснение. */
export const LandingCardRow = ({ children, label, note }: { children: ReactNode; label: string; note: string }) => (
  <div className="grid gap-1 border-t px-6 py-5">
    <span className={`${eyebrow} ${mutedText}`}>{label}</span>
    {children}
    <span className={`${bodyText} ${mutedText}`}>{note}</span>
  </div>
)

/** Синяя полоса вдоль верхнего края боковой карточки — единственный яркий штрих первого экрана. */
export const LandingCardStripe = () => (
  <div
    aria-hidden="true"
    className="h-[3px] w-full"
    style={{
      backgroundImage: 'linear-gradient(90deg, var(--primary), color-mix(in srgb, var(--primary) 15%, transparent))',
    }}
  />
)

type LandingAction = { href: string; label: string }

/**
 * Первый экран лендинга: плашка-надзаголовок, заголовок, вводный абзац, две кнопки и боковая
 * карточка. Что стоит между абзацем и кнопками, страница решает сама — там у одной перечисление
 * площадок, у другой три главных условия.
 */
export const LandingHero = ({
  aside,
  badge,
  extra,
  leadText,
  primaryAction,
  secondaryAction,
  title,
}: {
  aside: ReactNode
  badge: string
  extra: ReactNode
  leadText: string
  primaryAction: LandingAction
  secondaryAction: LandingAction
  title: ReactNode
}) => (
  <section className="relative grid items-start gap-12 py-12 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] lg:gap-x-[clamp(32px,6vw,88px)] lg:py-14">
    {/* Мягкое свечение за заголовком: страница начинается не с пустого белого листа. */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -top-28 -left-[22%] h-[520px] w-[68%]"
      style={{
        backgroundImage:
          'radial-gradient(52% 52% at 42% 40%, color-mix(in srgb, var(--primary) 10%, transparent) 0%, transparent 72%)',
      }}
    />

    <div className="relative grid justify-items-start">
      <Badge variant="info" size="lg" className="mb-5">
        <span aria-hidden="true" className="bg-primary size-[7px] rounded-full" />
        {badge}
      </Badge>

      <h1 className={display}>{title}</h1>

      <p className={`${lead} text-foreground/85 mt-6 max-w-[56ch]`}>{leadText}</p>

      {extra}

      <div className="mt-7 flex flex-wrap items-center gap-3">
        <Button size="lg" render={<Link href={primaryAction.href} />}>
          {primaryAction.label}
        </Button>
        {/* Вторая кнопка — secondary, а не outline: у outline в дизайн-системе бледно-серый текст,
            и рядом с синей кнопкой она читается как недоступная. */}
        <Button size="lg" variant="secondary" render={<Link href={secondaryAction.href} />}>
          {secondaryAction.label}
        </Button>
      </div>
    </div>

    {aside}
  </section>
)
